/**
 * klubb-club-state — global "selected club" state for the Klubb.no theme.
 *
 * Hydrates from a cookie at boot, exposes a small API on window.klubb,
 * broadcasts a 'change' event when the active club changes, and dispatches
 * document-level CustomEvents for picker open/close so any listener (the
 * picker modal, analytics, etc.) can react.
 *
 * Source of truth for selected club:
 *   1. Cookie 'klubb_active' (slug)
 *   2. The club itself: the page's own club in <script id="klubb-clubs-data" type="application/json">
 *      (zero or one club, R2 D70), else the member's stored copy, else the club directory (feed).
 *
 * Club directory (R2, D69): the clubs on the page are only the ones the page carries; any other
 * club is looked up with clubBySlug (sync) / whenClub (promise) / directory (all clubs), which
 * read the club feed (templates/search.klubb-clubs.liquid) and keep it in localStorage. The
 * member's own club is also stored (klubb_club_v1) so a later page can know it without the feed.
 * Stored copies are for display and attribution only; they never decide a price (rule 13).
 * A saved copy is trusted at boot but checked in the background (D73): when the stored list is older than
 * 6 hours or does not list the club, the feed is read, and a club the fresh feed no longer lists is dropped
 * (cookie, copy, active club) and the page is drawn again, instead of staying selected for ever.
 * A preview page (D75: a test theme with the preview switch on, marked data-klubb-preview) saves no list, asks the
 * feed every time and keeps the member's club under klubb_club_v1_preview, apart from the live theme's copies.
 *
 * Cookie semantics: 365 days, SameSite=Lax, Secure; written again on every page load
 * while the club is valid (D63: Safari caps JavaScript-written cookies at 7 days).
 */

(function (root) {
  'use strict';

  const COOKIE_NAME = 'klubb_active';
  const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

  // --- Cookie helpers ---
  function readCookie(name) {
    const raw = document.cookie || '';
    const parts = raw.split(';');
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i].trim();
      const eq = p.indexOf('=');
      const key = eq > -1 ? p.substr(0, eq) : p;
      if (key === name) {
        return eq > -1 ? decodeURIComponent(p.substr(eq + 1)) : '';
      }
    }
    return null;
  }
  function writeCookie(name, value) {
    const isHttps = (typeof location !== 'undefined') && location.protocol === 'https:';
    const secure = isHttps ? '; Secure' : '';
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; path=/' +
      '; max-age=' + COOKIE_MAX_AGE_SECONDS +
      '; SameSite=Lax' +
      secure;
  }
  function deleteCookie(name) {
    document.cookie = name + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }

  // --- Load club data injected by Liquid ---
  function loadClubsData() {
    const el = document.getElementById('klubb-clubs-data');
    if (!el) return [];
    try {
      const data = JSON.parse(el.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (e) {
      // Malformed — degrade silently
      return [];
    }
  }

  // D75 — a PREVIEW page is a test theme with the preview switch on (D67): the server marks it with
  // data-klubb-preview on the club data element. It sees clubs customers must not, and when a test theme is
  // shown on klubb.no (Shopify's preview link) it shares cookies and localStorage with the live theme, so what
  // each saves must not be read by the other. See the club directory block below.
  function isPreviewPage() {
    const el = document.getElementById('klubb-clubs-data');
    return !!(el && el.getAttribute && el.getAttribute('data-klubb-preview') === 'true');
  }

  function findClubBySlug(clubs, slug) {
    if (!slug) return null;
    for (let i = 0; i < clubs.length; i++) {
      if (clubs[i] && clubs[i].slug === slug) return clubs[i];
    }
    return null;
  }

  // --- Event bus (small, no dependencies) ---
  function createBus() {
    const handlers = {};
    return {
      on: function (event, handler) {
        if (!handlers[event]) handlers[event] = [];
        handlers[event].push(handler);
      },
      off: function (event, handler) {
        if (!handlers[event]) return;
        const idx = handlers[event].indexOf(handler);
        if (idx > -1) handlers[event].splice(idx, 1);
      },
      emit: function (event, payload) {
        const list = handlers[event] || [];
        for (let i = 0; i < list.length; i++) {
          try {
            list[i](payload);
          } catch (err) {
            // Don't let one handler block others; log for dev visibility.
            if (root && root.console && root.console.error) {
              root.console.error('[klubb] handler threw:', err);
            }
          }
        }
      },
    };
  }

  // --- Build the public API ---
  const clubs = loadClubsData();
  const bus = createBus();

  let activeClub = null;

  // --- Club directory (R2, D69) ---
  // Nothing in this block throws. A missing localStorage, a failed request or bad JSON just means
  // "club unknown for now", and every caller has a fallback.
  const FEED_URL = '/search?view=klubb-clubs';
  const FEED_MAX_PAGES = 40;              // 40 x 250 = 10 000 clubs; a runaway "next" cannot loop forever
  // D75: a preview page keeps NO saved list (it asks the feed every time, so a club added or flagged a moment
  // ago shows at once) and keeps the member's club under a key of its own. The live theme never reads that key,
  // so a club that only exists in preview cannot stay selected on the live site, and a list the live theme
  // saved cannot hide the preview clubs from a test theme. Only meant for test themes: customers never see it.
  const PREVIEW = isPreviewPage();
  const STORE_LIST = 'klubb_dir_v1';      // { savedAt, clubs }  the whole directory (never used on a preview page)
  const STORE_MEMBER = PREVIEW ? 'klubb_club_v1_preview' : 'klubb_club_v1'; // { savedAt, club }  the member's own club
  const LIST_TTL_MS = 6 * 60 * 60 * 1000; // an older stored list is still used, and refreshed in the background

  function storeGet(key) {
    try {
      const raw = root.localStorage && root.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function storeSet(key, value) {
    try { if (root.localStorage) root.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode or full: carry on without */ }
  }
  function storeRemove(key) {
    try { if (root.localStorage) root.localStorage.removeItem(key); } catch (e) { /* as above */ }
  }
  // The saved directory. On a preview page it is never read or written (D75).
  function readStoredList() { return PREVIEW ? null : storeGet(STORE_LIST); }
  function writeStoredList(list) { if (!PREVIEW) storeSet(STORE_LIST, { savedAt: Date.now(), clubs: list }); }

  // One feed page -> { clubs, next, pageParam, total } or null when it is not a feed page.
  // pageParam: Shopify pages a metaobject list with a generated query parameter (page_e9933e8a),
  // not `page`; the feed reports it and the next request must use it (28.10.2026: ?page=2 gave page 1 again).
  function parseFeedPage(text) {
    try {
      const page = JSON.parse(text);
      if (!page || !Array.isArray(page.clubs)) return null;
      const next = Number(page.next);
      return {
        clubs: page.clubs.filter(function (c) { return c && c.slug; }),
        next: next > 0 ? next : null,
        pageParam: typeof page.page_param === 'string' && page.page_param ? page.page_param : 'page',
        total: Number(page.total) || 0,
      };
    } catch (e) { return null; }
  }

  // Every feed page in turn (the feed says which is next; a page may hold fewer clubs than the
  // page size because preview-only clubs are left out after paging, so never count).
  function fetchDirectory() {
    const fetchFn = root.fetch;
    if (typeof fetchFn !== 'function') return Promise.reject(new Error('no fetch'));
    const seen = {};
    const all = [];
    function load(page, count, pageParam) {
      if (count >= FEED_MAX_PAGES) return Promise.reject(new Error('feed: too many pages'));
      return fetchFn(FEED_URL + (page > 1 ? '&' + encodeURIComponent(pageParam) + '=' + page : ''), { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('feed: ' + res.status);
          return res.text();
        })
        .then(function (text) {
          const parsed = parseFeedPage(text);
          if (!parsed) throw new Error('feed: bad page');
          parsed.clubs.forEach(function (c) { if (!seen[c.slug]) { seen[c.slug] = true; all.push(c); } });
          return parsed.next && parsed.next > page ? load(parsed.next, count + 1, parsed.pageParam) : all;
        });
    }
    return load(1, 0, 'page');
  }

  let memberCopy = storeGet(STORE_MEMBER); // { savedAt, club } of the member's own club
  let directoryList = null;                // the directory, once loaded or read from storage
  let directoryFresh = false;              // true once THIS page load fetched it from the feed
  let directoryLoad = null;                // the request in flight

  // The member's own club, for pages that do not carry it. Written only when it changed.
  function remember(club) {
    if (!club || !club.slug) return;
    if (memberCopy && memberCopy.club && JSON.stringify(memberCopy.club) === JSON.stringify(club)) return;
    memberCopy = { savedAt: Date.now(), club: club };
    storeSet(STORE_MEMBER, memberCopy);
  }
  function forget() {
    memberCopy = null;
    storeRemove(STORE_MEMBER);
  }

  // Fetches the feed again; concurrent callers share one request. Rejects on failure.
  function refreshDirectory() {
    if (directoryLoad) return directoryLoad;
    directoryLoad = fetchDirectory().then(function (list) {
      directoryList = list;
      directoryFresh = true;
      directoryLoad = null;
      writeStoredList(list);
      // The member's copy follows the feed, so a changed percentage or colour reaches pages that
      // do not carry the club themselves.
      const mine = activeClub ? findClubBySlug(list, activeClub.slug) : null;
      if (mine) remember(mine);
      dropGoneClub(list);
      bus.emit('directory', list);
      return list;
    }, function (err) {
      directoryLoad = null;
      throw err;
    });
    return directoryLoad;
  }

  // A club the feed no longer lists is gone (set to Draft, deleted, or hidden by preview_only): it must not
  // stay selected for ever on the strength of a saved copy (D73, 02.11.2026: v1.4.0 trusted the copy without
  // limit, so a gone club stayed selected and stamped every order from that browser). Only a feed fetched on THIS
  // page load can say so, and only a non-empty one (an empty list proves nothing, as in locate()). A club
  // this page carries is vouched for by the server and stays. The cookie is only deleted while it still names
  // the club: another tab may have picked a new one. Dropping the club emits 'change' (null) so the price
  // code and the picker follow, then the page is drawn again: its header and club prices were drawn for the
  // club, and a klubbpris on the screen that the cart will not give is worse than one reload. The reload
  // cannot repeat: it happens only when the cookie is really gone, and then nothing is left to drop.
  function dropGoneClub(list) {
    if (!list.length) return;
    function gone(slug) { return !!slug && !findClubBySlug(clubs, slug) && !findClubBySlug(list, slug); }
    const saved = storeGet(STORE_MEMBER) || memberCopy;
    if (saved && saved.club && gone(saved.club.slug)) forget(); // else a gone club could be picked again by a link
    if (activeClub && gone(activeClub.slug) && readCookie(COOKIE_NAME) === activeClub.slug) {
      activeClub = null;
      pageSlug = null;
      deleteCookie(COOKIE_NAME);
      bus.emit('change', null);
      if (readCookie(COOKIE_NAME) === null && root.location && typeof root.location.reload === 'function') root.location.reload();
    }
  }

  // The saved copy of the member's club is trusted at boot without waiting for the feed, so nothing would ever
  // ask whether the club is still published. Read the feed again when the stored list is older than
  // LIST_TTL_MS or does not list the club; a fresh stored list that lists it is proof enough (no request).
  // A failing feed leaves the club alone (D62: never get in a paying customer's way).
  function verifyActiveClub() {
    if (!activeClub || findClubBySlug(clubs, activeClub.slug)) return;
    const stored = readStoredList();
    const listed = stored && Array.isArray(stored.clubs) && findClubBySlug(stored.clubs, activeClub.slug);
    if (listed && Date.now() - stored.savedAt < LIST_TTL_MS) return;
    refreshDirectory().catch(function () {});
  }

  // All clubs. From memory, else storage (refreshed in the background when old), else the feed.
  // Never rejects: when nothing can be read it resolves to an empty list.
  function directory() {
    if (directoryList) return Promise.resolve(directoryList);
    const stored = readStoredList();
    if (stored && Array.isArray(stored.clubs) && stored.clubs.length) {
      directoryList = stored.clubs;
      if (!(Date.now() - stored.savedAt < LIST_TTL_MS)) refreshDirectory().catch(function () {});
      return Promise.resolve(directoryList);
    }
    return refreshDirectory().catch(function () { return []; });
  }

  // A club by slug without waiting: this page's clubs, the member's copy, the loaded directory.
  function clubBySlug(slug) {
    if (!slug) return null;
    const onPage = findClubBySlug(clubs, slug);
    if (onPage) return onPage;
    let copy = memberCopy;
    if (!copy || !copy.club || copy.club.slug !== slug) copy = storeGet(STORE_MEMBER); // another tab may have written it
    if (copy && copy.club && copy.club.slug === slug) { memberCopy = copy; return copy.club; }
    return directoryList ? findClubBySlug(directoryList, slug) : null;
  }

  // Every club this browser knows right now, without waiting: this page's, the member's own, the loaded
  // directory (once). For the cart code's pruning and for matching a product's club tokens.
  function knownClubs() {
    const out = [];
    const seen = {};
    function add(c) { if (c && c.slug && !seen[c.slug]) { seen[c.slug] = true; out.push(c); } }
    clubs.forEach(add);
    if (memberCopy && memberCopy.club) add(memberCopy.club);
    (directoryList || []).forEach(add);
    return out;
  }

  // Looks a slug up, loading the directory if needed. Resolves { club } when found, { absent: true }
  // when a feed fetched THIS page load does not list it (the club is gone or a Draft: a stale cookie),
  // { failed: true } when that could not be found out (feed unreachable, empty): then nothing is deleted.
  // A slug missing from a list that came from storage is looked for once more in a fresh feed (new club).
  function locate(slug) {
    const hit = clubBySlug(slug);
    if (hit) return Promise.resolve({ club: hit });
    return directory().then(function (list) {
      const found = findClubBySlug(list, slug);
      if (found) return { club: found };
      if (directoryFresh) return list.length > 0 ? { absent: true } : { failed: true };
      return refreshDirectory().then(function (fresh) {
        const again = findClubBySlug(fresh, slug);
        if (again) return { club: again };
        return fresh.length > 0 ? { absent: true } : { failed: true };
      }, function () { return { failed: true }; });
    });
  }

  // A club by slug, loading the directory if needed. Resolves null when unknown or unreachable.
  function whenClub(slug) {
    if (!slug) return Promise.resolve(null);
    return locate(slug).then(function (r) { return r.club || null; });
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      if (typeof root.setTimeout === 'function') root.setTimeout(resolve, ms); else resolve();
    });
  }

  // The club this page was drawn for. Another tab can switch the club (the
  // cookie is shared); pageClubChanged() tells the page it is out of date.
  let pageSlug = null;

  // A page carries only its own club (R2, D70), so a member's club is usually not in the page: it comes from
  // the stored copy (klubb_club_v1, written when the club was picked), which is there from the second visit.
  // The first visit without a copy has the cookie's slug but not the club yet: pendingSlug is set, activeClub
  // stays null, and ready() resolves once the directory has been asked. Code that must not run without the
  // member's club (every add to the cart, the checkout guard) waits for ready() first.
  let pendingSlug = null;
  let readyPromise = Promise.resolve(null);

  const cookieSlug = readCookie(COOKIE_NAME);
  if (cookieSlug) {
    const found = clubBySlug(cookieSlug);
    if (found) {
      activeClub = found;
      pageSlug = found.slug;
      remember(found);
      // D63 — renew on every visit. Safari caps a cookie written by JavaScript at 7 days from the
      // WRITE, whatever max-age says, so a cookie written once at the pick lapsed a week later
      // and the member silently became «no club» (full price, no order attribution). Writing the
      // same value again restarts the clock; a member who comes back at least weekly keeps the club.
      writeCookie(COOKIE_NAME, found.slug);
      verifyActiveClub();
    } else {
      pendingSlug = cookieSlug;
      readyPromise = locate(cookieSlug).then(function (r) {
        pendingSlug = null;
        // The member may have picked another club, or none, while we looked: only act on an unchanged cookie.
        if (readCookie(COOKIE_NAME) !== cookieSlug) return activeClub;
        if (r.club) {
          activeClub = r.club;
          pageSlug = r.club.slug;
          remember(r.club);
          writeCookie(COOKIE_NAME, r.club.slug); // D63 renewal, as above
          bus.emit('change', r.club);
        } else if (r.absent) {
          deleteCookie(COOKIE_NAME); // the feed does not know this club: a stale cookie
        } // failed: keep the cookie, try again on the next page
        return activeClub;
      });
    }
  }

  // Another tab may have changed or cleared the cookie since boot (18.10.2026):
  // re-read it on every access so an add-to-cart from an old tab stamps the
  // club the cart actually has. A change is broadcast once, after the caller.
  function syncFromCookie() {
    const slug = readCookie(COOKIE_NAME) || null;
    const current = activeClub ? activeClub.slug : null;
    if (slug === current) return;
    if (slug && slug === pendingSlug) return; // still being looked up
    const next = slug ? clubBySlug(slug) : null;
    if (slug && !next) return; // unknown slug: leave the state alone
    activeClub = next;
    if (next) remember(next);
    Promise.resolve().then(function () { bus.emit('change', next); });
  }

  const api = {
    get activeClub() { syncFromCookie(); return activeClub; },

    pageClubChanged: function () {
      syncFromCookie();
      return (activeClub ? activeClub.slug : null) !== pageSlug;
    },

    setActiveClub: function (slug) {
      const next = clubBySlug(slug);
      if (!next) return; // Unknown slug: no-op. Callers that may not have the club yet use whenClub(slug) first.
      pendingSlug = null;
      const same = activeClub && activeClub.slug === next.slug;
      activeClub = next;
      pageSlug = next.slug;
      writeCookie(COOKIE_NAME, next.slug);
      remember(next);
      if (!same) bus.emit('change', next);
    },

    clearActiveClub: function () {
      const had = activeClub !== null;
      activeClub = null;
      pageSlug = null;
      deleteCookie(COOKIE_NAME);
      forget();
      if (had) bus.emit('change', null);
    },

    // Club directory (R2, D69). clubBySlug is sync and may return null; whenClub and directory
    // wait for the feed and never reject; directory() lists every club, paged feed followed to the end.
    clubBySlug: clubBySlug,
    whenClub: whenClub,
    directory: directory,
    knownClubs: knownClubs,
    get pendingSlug() { return pendingSlug; },
    // Resolves with the member's club (or null) once it is known. At once when the club is known or there
    // is none; after the lookup on a first visit without a stored copy; after `timeoutMs` (default 4 s) at the
    // latest, so a slow or failing feed can never hold up a paying customer (D62: fail open).
    ready: function (timeoutMs) {
      if (!pendingSlug) return Promise.resolve(activeClub);
      return Promise.race([readyPromise, delay(timeoutMs == null ? 4000 : timeoutMs).then(function () { return activeClub; })]);
    },

    openPicker: function () {
      document.dispatchEvent(new CustomEvent('klubb:picker:open'));
    },

    closePicker: function () {
      document.dispatchEvent(new CustomEvent('klubb:picker:close'));
    },

    on: bus.on,
    // Kept for public-API symmetry with on(); currently only exercised by tests.
    off: bus.off,
  };

  root.klubb = api;

  // Coming back to a tab whose page was drawn for another club than the one
  // now in the cookie (switched in another tab): redraw it.
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && api.pageClubChanged() && root.location) {
        root.location.reload();
      }
    });
  }
})(typeof window !== 'undefined' ? window : globalThis);
